// src/pages/Admin/Views/Search.jsx

import React, { useState, useCallback, useRef } from 'react';
import { api } from '../../../services/apiServices'; 

// Import shadcn/ui components as per your guide 
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent } from '../../../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';

const Search = () => {
    // State management remains the same
    const [query, setQuery] = useState('');
    const [searchBy, setSearchBy] = useState('name_company');
    const [userType, setUserType] = useState('seller');
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const requestIdRef = useRef(0);

    // Corrected fetch logic
    const fetchResults = useCallback(async (currentPage, searchOptions) => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError('');
        try {
            const payload = { ...searchOptions, page: currentPage };
            const response = await api.post('/admin/manage-user/search/users', payload);

            if (requestId !== requestIdRef.current) return;
            
            setResults(prev => currentPage === 1 ? response.data.results : [...prev, ...response.data.results]);
            setHasMore(response.data.has_more);
            
            if (currentPage === 1 && response.data.results.length === 0) {
                setError('No users found matching your criteria.');
            }
        } catch (err) {
            if (requestId !== requestIdRef.current) return;

            setError(err.response?.data?.detail || 'An unexpected error occurred.');
            setResults([]);
            setHasMore(false);
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, []); // No dependencies needed here as all data is passed in

    const handleSearch = (e) => {
        e.preventDefault();
        const searchOptions = { query, search_by: searchBy, user_type: userType };
        setPage(1);
        fetchResults(1, searchOptions);
    };

    const handleUserTypeChange = (nextUserType) => {
        requestIdRef.current += 1;
        setUserType(nextUserType);
        setResults([]);
        setError('');
        setHasMore(false);
        setPage(1);
        setIsLoading(false);
    };

    const handleLoadMore = () => {
        const nextPage = page + 1;
        const searchOptions = { query, search_by: searchBy, user_type: userType };
        setPage(nextPage);
        fetchResults(nextPage, searchOptions);
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold text-foreground">Search Sellers / Buyers</h2>
                <p className="text-sm text-muted-foreground">Search for users by name, company, email, or phone number.</p>
            </div>

            <Card>
                <CardContent className="pt-6">
                    <form onSubmit={handleSearch} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-1 space-y-2">
                                <Label>Search By</Label>
                                <RadioGroup
                                    value={searchBy}
                                    onValueChange={setSearchBy}
                                    className="flex flex-wrap items-center gap-4"
                                    aria-label="Search By"
                                >
                                    <div className="flex items-center gap-2">
                                        <RadioGroupItem value="name_company" id="search-by-name-company" />
                                        <Label htmlFor="search-by-name-company">Name / Company</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <RadioGroupItem value="email" id="search-by-email" />
                                        <Label htmlFor="search-by-email">Email</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <RadioGroupItem value="phone" id="search-by-phone" />
                                        <Label htmlFor="search-by-phone">Phone</Label>
                                    </div>
                                </RadioGroup>
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor="query">Search Term</Label>
                                <Input id="query" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Enter search term..." />
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                             <div className="flex items-center gap-4">
                                <Label htmlFor="userType">User Type</Label>
                                <Select value={userType} onValueChange={handleUserTypeChange}>
                                    <SelectTrigger id="userType" className="w-32"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="seller">Seller</SelectItem>
                                        <SelectItem value="buyer">Buyer</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button type="submit" disabled={isLoading} variant="default">
                                {isLoading && page === 1 ? 'Searching...' : 'Search'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {error && <p className="text-sm text-destructive">{error}</p>}
            
            {results.length > 0 && (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{`${userType === 'seller' ? 'Seller ID' : 'Buyer ID'}`}</TableHead>
                            <TableHead>Name</TableHead>
                            {userType === 'seller' && <TableHead>Company</TableHead>}
                            {userType === 'seller' && <TableHead>PCS</TableHead>}
                            <TableHead>Email</TableHead>
                            <TableHead>Phone</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {results.map((user) => (
                            <TableRow key={user.seller_profile_id || user.email}>
                                <TableCell className="font-medium">
                                    <Button asChild variant="link" className="p-0 h-auto">
                                        <a href={`/admin/${userType === 'seller' ? 'sellers' : 'buyers'}/${userType === 'seller' ? user.seller_profile_id : user.buyer_id}/view`} target="_blank" rel="noopener noreferrer">
                                            {userType === 'seller' ? user.seller_profile_id : user.buyer_id}
                                        </a>
                                    </Button>
                                </TableCell>
                                <TableCell>{user.name || 'N/A'}</TableCell>
                                {userType === 'seller' && <TableCell className="font-medium">
                                    <Button asChild variant="link" className="p-0 h-auto">
                                      <a href={`/admin/sellers/${user.seller_profile_id}/view`} target="_blank" rel="noopener noreferrer">
                                        {user.company_name}
                                      </a>
                                    </Button>
                                </TableCell>}
                                {userType === 'seller' && <TableCell>
                                    <Badge variant="outline" className={`text-xs ${user.is_paid_seller ? 'bg-red-500 text-red-900' : 'bg-green-500 text-green-950'}`}>
                                        {user.is_paid_seller ? 'Yes' : 'No' || 'N/A'}
                                    </Badge>
                                </TableCell>}
                                <TableCell>{user.email || 'N/A'}</TableCell>
                                <TableCell>{user.phone || 'N/A'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {hasMore && !isLoading && (
                <div className="text-center">
                    <Button onClick={handleLoadMore}>Load More</Button>
                </div>
            )}
        </div>
    );
};

export default Search;
